import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { toast } from 'react-toastify';
import classes from './inventory.module.scss';
import Loader from '../../../_common/Loader/Loader';
import Pagination from '../../../_common/Pagination/Pagination';
import { getInventoryRequest } from '../../../../store/actions/products';
import accountHelpers from '../../../../utils/accountHelpers';
import Button from '../../../_common/Form/Buttons/Button';
import { RangeDatePicker } from '../../../_common/Form/DatePickers/RangeDatePicker/RangeDatePicker';
import { useQuery } from '../../../../utils/hooks/useQuery';
import api from '../../../../api/api';
import AttentionModal from '../../../_common/Modal/AttentionModal/AttentionModal';
import EditInventoryModal from './EditInventoryModal';
import { ReactComponent as EditIcon } from '../../../../assets/icons/edit.svg';
import { ReactComponent as DeleteIcon } from '../../../../assets/icons/delete.svg';

const header = ['Date', 'Reason', 'Change', 'Balance', 'Patient', 'RX', 'Memo', 'Lot', 'Expires', 'Actions'];

const getRowId = (d) => `${d.source}-${d.inventoryId}`;

const getRowUrl = (d) => (d.source === 'shipped' ? `/scripts/${d.sourceId}`
  : d.source === 'manualAdjust' ? `/products/adjustments/${d.sourceId}`
    : `/products/orders/${d.sourceId}`);

const formatNumber = (value) => (value === null || value === undefined || value === '' ? '-' : Number(value));

const thisMonthStartDay = moment().startOf('month').format('YYYY-MM-DD');
const thisMonthEndDate = moment().format('YYYY-MM-DD');

const Inventory = () => {
  const dispatch = useDispatch();
  const { productId } = useParams();
  const navigate = useNavigate();
  const { query, setQuery } = useQuery();

  const { inventoryList, inventoryTotalPage, requestStatus } = useSelector((store) => store.products);

  const [requestData, setRequestData] = useState({
    start: query.start || thisMonthStartDay,
    end: query.start && !query.end ? '' : query.end || thisMonthEndDate,
    page: query.page || 1,
    limit: query.limit || 15,
    productId,
    userId: accountHelpers.getUser().id,
  });
  const [downloadLoad, setDownloadLoad] = useState(false);
  const [editingInventory, setEditingInventory] = useState(null);
  const [deletingInventory, setDeletingInventory] = useState(null);
  const [deleteLoad, setDeleteLoad] = useState(false);

  useEffect(() => {
    (() => {
      dispatch(getInventoryRequest(requestData));

      setQuery({
        start: requestData.start,
        end: requestData.end,
        page: requestData.page,
        limit: requestData.limit,
      });
    })();
  }, [requestData]);

  const onChangeDate = (currentDate) => {
    setRequestData((prev) => ({
      ...prev, start: currentDate[0], end: currentDate[1], page: 1,
    }));
  };

  const clearFilters = () => {
    setRequestData((prev) => ({
      ...prev, start: thisMonthStartDay, end: thisMonthEndDate, page: 1,
    }));
  };

  const onChangePageData = (path, count) => {
    setRequestData((prev) => ({ ...prev, [path]: count, ...(path === 'limit' ? { page: 1 } : {}) }));
  };

  const downloadFile = async () => {
    setDownloadLoad(true);

    await api.getInventory({
      ...requestData,
      page: undefined,
      limit: undefined,
      download: true,
    });

    setDownloadLoad(false);
  };

  const saveInventory = async () => {
    await dispatch(getInventoryRequest(requestData));

    setEditingInventory(null);
  };

  const deleteInventory = async () => {
    setDeleteLoad(true);

    try {
      await api.deleteInventory(deletingInventory.inventoryId);

      if (inventoryList.length === 1 && +requestData.page > 1) {
        setRequestData((prev) => ({ ...prev, page: +prev.page - 1 }));
      } else {
        await dispatch(getInventoryRequest(requestData));
      }

      setDeletingInventory(null);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Something went wrong');
    }

    setDeleteLoad(false);
  };

  return (
    <div className={classes.table_pagination_wrapper}>
      <div className={classes.title}>
        <div className={classes.title_left_block}>
          <h2>Inventory</h2>

          <div className={classes.calendar}>
            <RangeDatePicker
              currentDate={requestData}
              onChangeDate={onChangeDate}
            />
          </div>

          <div style={{ width: 160, marginRight: 15 }}>
            <Button
              title="Clear"
              clearButton
              onClick={clearFilters}
            />
          </div>
        </div>

        <Button
          title="Download"
          downloadButton
          onClick={downloadFile}
          loading={downloadLoad}
        />
      </div>

      { requestStatus === 'request'
        ? (
          <div className={classes.loading_wrapper}>
            <Loader />
          </div>
        )

        : requestStatus === 'success' && !inventoryList.length
          ? <div className={classes.notResult}>No Data Found</div>

          : (
            <>
              <div className={classes.table_wrapper}>
                <table>
                  <thead>
                    <tr>
                      {header.map((h) => <th key={h}>{h}</th>)}
                    </tr>
                  </thead>

                  <tbody>
                    {inventoryList?.map((d) => {
                      const id = getRowId(d);

                      const url = getRowUrl(d);

                      return (
                        <tr key={id} onClick={() => navigate(url)}>
                          <td>
                            {d.date ? moment(d.date).format('MM/DD/YYYY') : '-'}
                          </td>

                          <td>{d.reason || '-'}</td>

                          <td>{formatNumber(d.change)}</td>

                          <td>{formatNumber(d.balance)}</td>

                          <td>{d.patient || '-'}</td>

                          <td>{d.rxNumber || '-'}</td>

                          <td>{d.memo || '-'}</td>

                          <td>{d.lot || '-'}</td>

                          <td>{d.expiration ? moment(d.expiration).format('MM/DD/YYYY') : '-'}</td>

                          <td onClick={(e) => e.stopPropagation()}>
                            <div className={classes.actions}>
                              {d.reason !== 'Shipped' && (
                                <button
                                  type="button"
                                  onClick={() => setEditingInventory(d)}
                                >
                                  <EditIcon className={classes.edit} />
                                </button>
                              )}

                              <button
                                type="button"
                                className={classes.delete_btn}
                                onClick={() => setDeletingInventory(d)}
                              >
                                <DeleteIcon />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <Pagination
                page={+requestData.page - 1}
                limit={+requestData.limit}
                totalPage={inventoryTotalPage}
                onChange={onChangePageData}
              />
            </>
          )}

      <EditInventoryModal
        inventory={editingInventory}
        onClose={() => setEditingInventory(null)}
        onSave={saveInventory}
      />

      <AttentionModal
        isOpen={!!deletingInventory}
        onClose={() => setDeletingInventory(null)}
        onSave={deleteInventory}
        title="Delete inventory?"
        text="Are you sure you want to delete this inventory?"
        loading={deleteLoad}
      />
    </div>
  );
};

Inventory.propTypes = {

};

export default Inventory;
